import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AppUtils } from 'src/app/helpers/app.utils';
import { PageUrls } from 'src/app/helpers/page-urls';
import { isNullOrUndefined } from 'util';
import { UserService } from 'src/app/admin/services/user.service';
import { PaymentService } from 'src/app/admin/services/payment.service';
import { TableModel } from 'src/app/models/table.model';
import { SelectionFilterModel } from 'src/app/admin/models/selection.filter.model';
import { SelectListItemModel } from 'src/app/models/select.list.item.model';
import { PagedContributorPaymentDetail } from 'src/app/admin/models/paged.contributor.payment.detail';
import { ContributorPaymentFilterModel } from 'src/app/admin/models/contributor.payment.filter.model';
import { ContributorPaymentUpsertModel } from 'src/app/admin/models/contributer.payment.upsert.model';
import { ContributorPaymentModel } from 'src/app/admin/models/contributor.payment.model';

@Component({
    selector: 'app-contributor-payment',
    templateUrl: './contributor.payment.history.component.html'
})
export class ContributorPaymentHistoryComponent implements OnInit {
    subscriptions: Subscription[];
    totalRecords: number;
    tableModel: TableModel;
    pagedContributorPaymentDetails: PagedContributorPaymentDetail = new PagedContributorPaymentDetail();
    isModelLoaded = false;
    contributors: Array<SelectListItemModel>;
    contributorSelectionFilter = new SelectionFilterModel<ContributorPaymentFilterModel>();
    selectedContributor: SelectListItemModel;
    paymentFilterModel = new ContributorPaymentFilterModel();
    paymentModel = new ContributorPaymentUpsertModel();

    pbMaxVal = 0;
    pbCurrentVal = 0;
    isProcessingPayment = false;
    showMakePaymentButton = false;
    disableSelectAll = false;

    years: Array<number>;
    months: Array<string>;

    get urls() {
        return PageUrls;
    }

    constructor(private ngxSpinnerService: NgxSpinnerService,
        private toastr: ToastrService,
        private userService: UserService,
        private paymentService: PaymentService) {
        this.selectedContributor = this.getDefaultContributor();
    }

    ngOnInit() {
        this.years = AppUtils.getYears();

        this.tableModel = new TableModel();
        this.tableModel.orderBy = 'name';
        this.tableModel.orderByDirection = 'asc';
        this.totalRecords = 0;
        this.subscriptions = [];
        this.initSubscribers();
        this.getContributorSelectItems();
        this.getContributorPaymentHistory();
    }

    getDefaultContributor() {
        const defaultContributor = new SelectListItemModel();
        defaultContributor.key = null;
        defaultContributor.value = 'All';
        return defaultContributor;
    }

    initSubscribers() {
        const subscription = this.tableModel.pageChanged.subscribe(() => this.getContributorPaymentHistory());
        this.subscriptions.push(subscription);
    }

    getContributorSelectItems = () => {
        this.userService.getContributorSelectItems()
            .subscribe(response => {
                this.contributors = response;
            }, error => {
                AppUtils.processErrorResponse(this.toastr, error);
            });
    }

    setContributor = (contributor: SelectListItemModel) => {
        this.selectedContributor = contributor || this.getDefaultContributor();
        this.resetTableModel();
    }

    getYearMonthSerialized() {
        if (isNullOrUndefined(this.paymentFilterModel.year) || this.paymentFilterModel.year === '') {
            this.months.splice(0, this.months.length);
            this.paymentFilterModel.month = '';
        } else {
            this.months = AppUtils.getMonths(Number(this.paymentFilterModel.year));
        }
        this.getContributorPaymentHistory();
    }

    getContributorPaymentHistory = () => {
        this.ngxSpinnerService.show();
        this.isModelLoaded = false;
        this.ngxSpinnerService.show();
        if (isNullOrUndefined(this.selectedContributor.key)) {
            this.paymentFilterModel.contributorId = '';
        } else {
            this.paymentFilterModel.contributorId = this.selectedContributor.key;
        }
        this.paymentService.GetContributorPayments(this.paymentFilterModel, this.tableModel)
            .subscribe(data => {
                this.pagedContributorPaymentDetails = data;
                this.totalRecords = data.totalCount;
                if (this.pagedContributorPaymentDetails.items.filter(x => x.stripeAccountStatus === 4).length === 0) {
                    this.disableSelectAll = true;
                }
                this.contributorSelectionFilter.selectedAll = false;
                this.isModelLoaded = true;
                this.ngxSpinnerService.hide();
            }, error => {
                this.ngxSpinnerService.hide();
                this.isModelLoaded = true;
                AppUtils.processErrorResponse(this.toastr, error);
            });
    }

    selectAll() {
        this.pagedContributorPaymentDetails.items.forEach(item => {
            if (item.stripeAccountStatus !== null || item.stripeAccountStatus === 4) {
                item.isSelected = this.contributorSelectionFilter.selectedAll;
                this.showMakePaymentButton = this.contributorSelectionFilter.selectedAll;
            }
        });
        if (this.contributorSelectionFilter.selectedAll) {
            this.contributorSelectionFilter.deselectedItems = new Array<string>();
        } else {
            this.contributorSelectionFilter.selectedItems = new Array<string>();
        }
    }

    updateSelectAll() {
        if (this.pagedContributorPaymentDetails.items.filter(x => x.isSelected).length === 0) {
            this.contributorSelectionFilter.selectedAll = false;
            this.showMakePaymentButton = false;
        } else {
            this.showMakePaymentButton = true;
        }
    }

    async makePayment() {
        const selectedContributors = this.pagedContributorPaymentDetails.items.filter(x => x.isSelected);
        if (!selectedContributors || selectedContributors.length === 0) {
            this.toastr.error('Please select a photographer to continue');
            return;
        }
        if (!confirm('Are you sure you want to process payment for selected contributors?')) {
            return;
        }
        this.isProcessingPayment = true;
        let isAnyPaymentFailed = false;
        this.pbMaxVal = selectedContributors.length;
        this.pbCurrentVal = 0;
        this.ngxSpinnerService.show();
        for (const contributor of selectedContributors) {
            try {
                await this.addContributorPayment(contributor).then(() => {
                    this.pbCurrentVal += 1;
                }).catch(() => {
                    isAnyPaymentFailed = true;
                });
            } catch (e) {
                console.log(e);
            }
        }

        setTimeout(() => {
            this.ngxSpinnerService.hide();
            if (!isAnyPaymentFailed) {
                this.toastr.success('Selected contributor\'s payments has been processed successfully');
            } else if (isAnyPaymentFailed) {
                this.toastr.error('Some payments can not be processed. Please try again');
            }
            this.isProcessingPayment = false;
            this.getContributorPaymentHistory();
        }, 1000);
    }

    async addContributorPayment(model: ContributorPaymentModel): Promise<any> {
        return new Promise((resolve, reject) => {
            this.paymentModel = new ContributorPaymentUpsertModel();
            this.paymentModel.amount = model.remainingAmount;
            this.paymentModel.contributorId = model.id;
            this.paymentModel.remark = 'ATP Images Contributor Payment';
            this.paymentService.addContributorPaymentLog(this.paymentModel).subscribe(
                () => {
                    resolve(true);
                }, error => {
                    AppUtils.processErrorResponse(this.toastr, error);
                    reject(error);
                });
        });
    }

    resetTableModel(): void {
        this.tableModel.page = 1;
    }
}
